import { Component, HostListener, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTabsModule } from '@angular/material/tabs';
import { subDays } from 'date-fns';
import { LegendPosition } from '@swimlane/ngx-charts';
import { InputComponent } from '../../components/input/input.component';
import { AvailableJobsChartComponent } from './charts/available-jobs-chart/available-jobs-chart.component';
import { AvailableCandidatesChartComponent } from './charts/available-candidates-chart/available-candidates-chart.component';
import { ApplicationsChartComponent } from './charts/applications-chart/applications-chart.component';
import { CreatedJobsChartComponent } from './charts/created-jobs-chart/created-jobs-chart.component';
import { RegisteredCandidatesChartComponent } from './charts/registered-candidates-chart/registered-candidates-chart.component';
import { HiringRateChartComponent } from './charts/hiring-rate-chart/hiring-rate-chart.component';
import { SupplyDemandChartComponent } from './charts/supply-demand-chart/supply-demand-chart.component';
import { MarketSummaryDashboardComponent } from './charts/market-summary-dashboard/market-summary-dashboard.component';
import { AnalyticsFilters } from '../../models/analytics.models';

/**
 * Market Analytics Page Component
 * Main dashboard for labor market analytics with filters and tabbed charts
 */
@Component({
  selector: 'app-market-analytics-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatButtonModule,
    MatTabsModule,
    InputComponent,
    AvailableJobsChartComponent,
    AvailableCandidatesChartComponent,
    ApplicationsChartComponent,
    CreatedJobsChartComponent,
    RegisteredCandidatesChartComponent,
    HiringRateChartComponent,
    SupplyDemandChartComponent,
    MarketSummaryDashboardComponent,
  ],
  templateUrl: './market-analytics-page.component.html',
  styleUrls: ['./market-analytics-page.component.scss'],
})
export class MarketAnalyticsPageComponent implements OnInit {
  filterForm!: FormGroup;
  filters: AnalyticsFilters;
  selectedTabIndex = 0;

  periods = [
    { value: 'day', label: 'analytics.periodDaily' },
    { value: 'week', label: 'analytics.periodWeekly' },
    { value: 'month', label: 'analytics.periodMonthly' },
  ];

  // Responsive legend position shared by all charts
  legendPosition: LegendPosition = LegendPosition.Right;
  private readonly MOBILE_BREAKPOINT = 768;
  private readonly platformId = inject(PLATFORM_ID);

  constructor(private fb: FormBuilder) {
    // Initialize default filters: last 30 days, daily period
    this.filters = {
      dateFrom: subDays(new Date(), 30),
      dateTo: new Date(),
      period: 'day',
    };
  }

  ngOnInit(): void {
    this.initFilterForm();
    // Set initial legend position based on screen size
    this.updateLegendPosition();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?: Event) {
    this.updateLegendPosition();
  }

  private updateLegendPosition(): void {
    // `window` doesn't exist during SSR — fall back to the desktop default
    // and let the resize listener take over once the browser hydrates.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const width = window.innerWidth;
    this.legendPosition = width <= this.MOBILE_BREAKPOINT ? LegendPosition.Below : LegendPosition.Right;
  }

  /**
   * Initialize filter form with default values
   */
  private initFilterForm(): void {
    this.filterForm = this.fb.group({
      dateFrom: [this.filters.dateFrom],
      dateTo: [this.filters.dateTo],
      period: [this.filters.period],
      categoryId: [null],
      positionId: [null],
      city: [''],
      country: [''],
    });
  }

  /**
   * Apply filters and update chart
   */
  applyFilters(): void {
    const formValue = this.filterForm.value;

    this.filters = {
      dateFrom: formValue.dateFrom || subDays(new Date(), 30),
      dateTo: formValue.dateTo || new Date(),
      period: formValue.period || 'day',
      categoryId: formValue.categoryId || undefined,
      positionId: formValue.positionId || undefined,
      city: formValue.city || undefined,
      country: formValue.country || undefined,
    };
  }

  /**
   * Reset filters to defaults
   */
  resetFilters(): void {
    this.filters = {
      dateFrom: subDays(new Date(), 30),
      dateTo: new Date(),
      period: 'day',
    };

    this.filterForm.reset({
      dateFrom: this.filters.dateFrom,
      dateTo: this.filters.dateTo,
      period: this.filters.period,
      categoryId: null,
      positionId: null,
      city: '',
      country: '',
    });
  }

  /**
   * Quick filter: Last 7 days
   */
  setLast7Days(): void {
    this.filterForm.patchValue({
      dateFrom: subDays(new Date(), 7),
      dateTo: new Date(),
    });
    this.applyFilters();
  }

  /**
   * Quick filter: Last 30 days
   */
  setLast30Days(): void {
    this.filterForm.patchValue({
      dateFrom: subDays(new Date(), 30),
      dateTo: new Date(),
    });
    this.applyFilters();
  }

  /**
   * Quick filter: Last 90 days
   */
  setLast90Days(): void {
    this.filterForm.patchValue({
      dateFrom: subDays(new Date(), 90),
      dateTo: new Date(),
    });
    this.applyFilters();
  }
}
